import styles from 'css/src/components/app/template-preview/TemplateScenePreviews.module.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Luminary, { AspectRatio, VideoData } from '@lumen5/luminary';

const aspectRatioStyles: Record<string, string> = {
    [AspectRatio.LANDSCAPE]: styles['landscape'],
    [AspectRatio.SQUARE]: styles['square'],
    [AspectRatio.VERTICAL]: styles['vertical'],
    [AspectRatio.FOURFIVE]: styles['fourfive'],
};

import SlideVideoPreview from 'js/src/components/app/SlideVideoPreview';
import { getBrandKitById } from 'js/src/data/brand-kits';
import { getFontList } from 'js/src/data/fonts';
import {
    getTemplatePreviewSceneDesigns,
    getTemplateBundle,
    getTemplateColorPalettes,
    isTemplateBundleLoading,
    loadTemplateAndSceneDesigns,
} from 'js/src/data/templates';
import { FullscreenSceneSelectorLuminaryConfigOverrides, ImmutableToJS } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { DEFAULT_LUMINARY_CONFIG } from 'js/src/libs/video-data-consts';
import { applyBrandKitToLuminary } from 'js/src/luminary/brand-kit-operations';
import { useLuminaryLoaded } from 'js/src/luminary/luminary-hooks';
import type { LuminarySceneImageRenderingObserver } from 'js/src/luminary/luminary-scene-image/luminary-scene-image-rendering-observer';
import { useLuminarySceneImageRenderingObserver } from 'js/src/luminary/luminary-scene-image/luminary-scene-image-rendering-observer';

type TemplateScenePreviewsProps = {
    templateId: string;
    aspectRatio: AspectRatio;
    isVisible: boolean;
    selectedPaletteIndex: number | null;
    selectedBrandKitId: number | null;
    maxScenes?: number;
};

type ScenePreviewItemProps = {
    sceneId: string;
    aspectRatio: AspectRatio;
    luminaryRef: React.MutableRefObject<Luminary>;
    renderingObserver: LuminarySceneImageRenderingObserver;
    isLoading: boolean;
};

const ScenePreviewItem: React.FC<ScenePreviewItemProps> = ({
    sceneId,
    aspectRatio,
    luminaryRef,
    renderingObserver,
    isLoading,
}) => {
    const [hasCanvas, setHasCanvas] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (contentRef.current?.querySelector('canvas')) {
                setHasCanvas(true);
                observer.disconnect();
            }
        });

        if (contentRef.current) {
            // Check immediately
            if (contentRef.current.querySelector('canvas')) {
                setHasCanvas(true);
            } else {
                // Watch for canvas to be added
                observer.observe(contentRef.current, {
                    childList: true,
                    subtree: true,
                });
            }
        }

        return () => observer.disconnect();
    }, []);

    return (
        <div className={classNames(styles['scene-preview-item'], aspectRatioStyles[aspectRatio])}>
            <div className={styles['scene-preview-placeholder']} />
            <div
                ref={contentRef}
                className={classNames(styles['scene-preview-content'], {
                    [styles['rendered']]: hasCanvas,
                })}
            >
                <SlideVideoPreview
                    renderingObserver={renderingObserver}
                    startSceneId={sceneId}
                    endSceneId={sceneId}
                    aspectRatio={aspectRatio}
                    luminaryRef={luminaryRef}
                    disableHoverPreview={isLoading}
                />
            </div>
        </div>
    );
};

const TemplateScenePreviews: React.FC<TemplateScenePreviewsProps> = ({
    templateId,
    aspectRatio,
    isVisible,
    selectedPaletteIndex,
    selectedBrandKitId,
    maxScenes,
}) => {
    const dispatch = useDispatch();
    const [sceneIds, setSceneIds] = useState<string[]>([]);
    const [rebuildScenes, setRebuildScenes] = useState(true);

    // Load template data when component becomes visible
    useEffect(() => {
        if (isVisible && templateId) {
            dispatch(loadTemplateAndSceneDesigns(templateId));
        }
    }, [dispatch, isVisible, templateId]);

    // Redux selectors for scene data
    const sceneDesigns = useSelector((state: RootState) =>
        getTemplatePreviewSceneDesigns(state, templateId),
    );
    const templateBundle = useSelector((state: RootState) => getTemplateBundle(state, templateId));
    const isTemplateBundleLoadingFlag = useSelector((state: RootState) =>
        isTemplateBundleLoading(state, templateId),
    );
    const colorPalettes = useSelector((state: RootState) =>
        getTemplateColorPalettes(state, templateId),
    );
    const brandKit = useSelector((state: RootState) =>
        selectedBrandKitId ? getBrandKitById(state, selectedBrandKitId) : undefined,
    );
    const fontList = useSelector(getFontList);

    // Check if main luminary is loaded
    const { luminaryLoaded: appLuminaryLoaded } = useLuminaryLoaded();

    // Create dedicated luminary instance for scene previews
    const luminary = useState<Luminary>(() => {
        return new Luminary({
            ...DEFAULT_LUMINARY_CONFIG,
            ...FullscreenSceneSelectorLuminaryConfigOverrides(),
        });
    })[0];
    const luminaryRef = useRef(luminary);

    // Cleanup luminary on unmount or when modal closes
    useEffect(() => {
        if (!isVisible) {
            // Stop any ongoing rendering when modal closes
            luminaryRef.current.stop();
            luminaryRef.current.canvasParent = null;
        }
    }, [isVisible]);

    // Cleanup luminary on unmount
    useEffect(() => {
        const luminaryInstance = luminaryRef.current;
        return () => {
            luminaryInstance.stop();
            luminaryInstance.canvasParent = null;
            luminaryInstance.removeAllSceneGroups();
            luminaryInstance.destroy();
        };
    }, []);

    // Set up luminary template data
    useEffect(() => {
        if (isTemplateBundleLoadingFlag) {
            return;
        }
        if (!templateBundle) {
            return;
        }
        if (!appLuminaryLoaded) {
            return;
        }
        if (luminaryRef.current.aspectRatio !== aspectRatio) {
            setRebuildScenes(true);
        }
        if (
            luminaryRef.current.template.id !== templateId &&
            !luminaryRef.current.canChangeTemplates
        ) {
            luminaryRef.current.removeAllSceneGroups();
            setRebuildScenes(true);
        }
        luminaryRef.current.setTemplate(templateBundle);
        luminaryRef.current.template.transitionsEnabled = false;
        luminaryRef.current.aspectRatio = aspectRatio;

        // Apply brand kit (colors, fonts, watermarks) or selected color palette
        if (selectedBrandKitId !== null && brandKit) {
            applyBrandKitToLuminary(luminaryRef.current, brandKit.toJS(), fontList);
        } else if (selectedPaletteIndex !== null && colorPalettes.size > 0) {
            const palette = colorPalettes.get(selectedPaletteIndex);
            if (palette) {
                const colors = ImmutableToJS(palette.get('colors'));
                luminaryRef.current.template.colors = colors;
            }
        }
    }, [
        appLuminaryLoaded,
        aspectRatio,
        isTemplateBundleLoadingFlag,
        templateBundle,
        selectedPaletteIndex,
        colorPalettes,
        selectedBrandKitId,
        brandKit,
        fontList,
        templateId,
    ]);

    // Build scenes for preview
    const buildScenes = useCallback(() => {
        if (!luminaryRef.current.isTemplateBundleSet) {
            return false;
        }
        if (!appLuminaryLoaded) {
            return false;
        }
        if (sceneDesigns.length === 0) {
            return false;
        }

        const allSceneIds: string[] = [];
        luminaryRef.current.batchRun(() => {
            sceneDesigns.forEach((sceneDesign) => {
                const scene = luminaryRef.current.addScene({
                    sceneDesignId: sceneDesign.id,
                });
                // Only include scenes they can pick
                if (
                    scene.sceneCategory === VideoData.CATEGORY_ORDINARY ||
                    scene.sceneCategory === VideoData.CATEGORY_COMMON ||
                    scene.sceneCategory === VideoData.CATEGORY_TALKING_HEAD_INSERT ||
                    scene.sceneCategory === VideoData.CATEGORY_VOICEOVER ||
                    scene.sceneCategory === VideoData.CATEGORY_OUTRO
                ) {
                    allSceneIds.push(scene.id);
                }
            });
        });
        setSceneIds(allSceneIds);
        return true;
    }, [appLuminaryLoaded, sceneDesigns]);

    // Rebuild scenes when modal becomes visible or aspect ratio changes
    useEffect(() => {
        if (!isVisible) {
            return;
        }
        if (!rebuildScenes) {
            return;
        }
        if (!luminaryRef.current || !luminaryRef.current.isTemplateBundleSet) {
            return;
        }

        luminaryRef.current.removeAllSceneGroups();
        setRebuildScenes(!buildScenes());
    }, [buildScenes, isVisible, rebuildScenes]);

    // Scene rendering observer
    const { renderingObserver, renderingIsInProgress } = useLuminarySceneImageRenderingObserver();

    // Show loading state if we haven't built scenes yet
    const showLoadingPlaceholders = sceneIds.length === 0;

    // Determine number of columns based on aspect ratio
    // Portrait/vertical/square formats: more columns since they're taller
    // Landscape: fewer columns since it's wider
    const getColumnsPerRow = (aspectRatioStr: AspectRatio): number => {
        if (
            aspectRatioStr === AspectRatio.VERTICAL ||
            aspectRatioStr === AspectRatio.FOURFIVE ||
            aspectRatioStr === AspectRatio.SQUARE
        ) {
            return 4; // More columns for tall formats
        }
        return 3; // landscape
    };

    const columnsPerRow = getColumnsPerRow(aspectRatio);

    // Use actual scene IDs or placeholders
    // Show 20 placeholders initially to match typical template scene count
    const displaySceneIds = useMemo(() => {
        const ids = showLoadingPlaceholders
            ? Array.from({ length: 20 }, (_, i) => `placeholder-${i}`)
            : sceneIds;
        return maxScenes ? ids.slice(0, maxScenes) : ids;
    }, [showLoadingPlaceholders, sceneIds, maxScenes]);

    const renderScenePreview = (sceneId: string) => {
        // Render placeholder for loading state
        if (sceneId.startsWith('placeholder-')) {
            return (
                <div
                    key={sceneId}
                    className={classNames(
                        styles['scene-preview-item'],
                        aspectRatioStyles[aspectRatio],
                    )}
                >
                    <div className={styles['scene-preview-placeholder']} />
                </div>
            );
        }

        const scene = luminaryRef.current.getSceneById(sceneId);
        if (!scene) return null;

        return (
            <ScenePreviewItem
                key={sceneId}
                sceneId={sceneId}
                aspectRatio={aspectRatio}
                luminaryRef={luminaryRef}
                renderingObserver={renderingObserver}
                isLoading={renderingIsInProgress}
            />
        );
    };

    return (
        <div
            className={classNames(styles['scene-previews-section'], {
                [styles['is-loading']]: renderingIsInProgress || showLoadingPlaceholders,
            })}
        >
            <div
                className={styles['scene-previews-grid']}
                style={{
                    gridTemplateColumns: `repeat(${columnsPerRow}, 1fr)`,
                }}
            >
                {displaySceneIds.map((sceneId) => renderScenePreview(sceneId))}
            </div>
        </div>
    );
};

export default TemplateScenePreviews;
